import { ReactNode } from 'react'

import { cn } from 'utils/classMerge'

import * as Primitive from './primitive'
import { CardRadio } from './variants/card-radio'
import { SimpleRadio } from './variants/simple-radio'

export interface RadioOption {
  /** Id to be used in the radio group */
  id: string | number
  /** Title to display on the left side of the radio */
  title: string
  /** Description to display on the left side of the radio, below the title */
  description?: string
  /** Right text to display on the right side of the radio */
  rightText?: string
  /** Right text helper to display on the right side of the radio */
  rightTextHelper?: string
  /** Value to be used in the radio group. If not provided, the id will be used */
  value?: string | number
  /** Icon to display on the left side of the radio */
  icon?: ReactNode
  /** Custom label to display on the left side of the radio */
  customLabel?: ReactNode
}
interface RadioGroupProps {
  /** Options to display in the radio group */
  options: RadioOption[]
  /** Default value to be used in the radio group */
  defaultValue?: string
  /** Variant of the radio group */
  variant?: 'simple' | 'card'
  /** Name of the radio group */
  name: string
  /** Callback function to handle the change of the selected option */
  onChange?: (value: string) => void
  /** Class name for the container */
  className?: string
  /** Whether the radio group is disabled */
  disabled?: boolean
  /** Id of the selected option */
  selectedOption?: string | number
}

const RADIO_VARIANTS = {
  card: CardRadio,
  simple: SimpleRadio,
} as const

const RadioGroup = ({
  options,
  defaultValue,
  variant = 'simple',
  name,
  onChange,
  className,
  disabled,
  selectedOption,
}: RadioGroupProps) => {
  const RadioVariant = RADIO_VARIANTS[variant]

  return (
    <Primitive.Root
      defaultValue={defaultValue}
      onValueChange={onChange}
      name={name}
      className={cn('tw-grid tw-gap-3', className, {
        'tw-pointer-events-none tw-opacity-50': disabled,
      })}
      disabled={disabled}
    >
      {options.map((option) => (
        <div
          key={option.id}
          className={cn({
            'tw-relative': variant === 'card',
            className,
          })}
        >
          <RadioVariant
            option={option}
            {...(variant === 'simple' &&
              selectedOption !== undefined && {
                isChecked: option.value === selectedOption,
              })}
          />
        </div>
      ))}
    </Primitive.Root>
  )
}

export default RadioGroup
